import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Trash, PaperPlaneTilt, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CAMPAIGN_AUDIENCES, RECIPIENT_STATUS } from "@/lib/constants";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  completed: "bg-blue-100 text-blue-800 border-blue-200",
  paused: "bg-amber-100 text-amber-800 border-amber-200",
};

export default function Campaigns() {
  const [items, setItems] = useState(null);

  const load = () => api.get("/coll/campaigns").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (c, status) => {
    try { await api.put(`/coll/campaigns/${c.id}`, { status }); toast.success("Campaign updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/coll/campaigns/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!items) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading campaigns…</div>;

  const recipients = items.reduce((s, c) => s + (c.recipients?.length || 0), 0);
  const converted = items.reduce((s, c) => s + (c.recipients || []).filter((r) => r.status === "converted").length, 0);

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <PageHeader
        overline="AI Outreach" title="Outreach Campaigns"
        subtitle="Every outreach effort, active and past"
        action={<Button asChild className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"><Link to="/app/outreach/new" data-testid="new-campaign-btn"><Plus size={16} className="mr-1" /> New Campaign</Link></Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Campaigns" value={items.length} testid="stat-campaigns" />
        <Stat label="Active" value={items.filter((c) => c.status === "active").length} tone="text-emerald-600" testid="stat-active-campaigns" />
        <Stat label="Recipients" value={recipients} testid="stat-recipients" />
        <Stat label="Converted to jobs" value={converted} tone="text-accent" testid="stat-converted" />
      </div>

      {items.length === 0 ? (
        <EmptyState icon={Megaphone}>No campaigns yet. <Link to="/app/outreach/new" className="text-accent hover:underline">Create your first campaign</Link> and let AI draft the pitch.</EmptyState>
      ) : (
        <div className="space-y-3">
          {items.map((c) => {
            const rec = c.recipients || [];
            const conv = rec.filter((r) => r.status === "converted").length;
            return (
              <Card key={c.id} className="p-5 border-border transition-transform duration-200 hover:-translate-y-0.5 hover:shadow" data-testid={`campaign-${c.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Megaphone size={18} weight="duotone" className="text-accent shrink-0" />
                      <h3 className="font-display font-semibold text-lg text-primary truncate">{c.name}</h3>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {CAMPAIGN_AUDIENCES[c.audience] || c.audience} · <span className="capitalize">{c.channel}</span> · {rec.length} recipients
                      {c.scheduled_for ? ` · scheduled ${c.scheduled_for}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold uppercase tracking-wider ${STATUS_TONE[c.status] || STATUS_TONE.draft}`}>{c.status || "draft"}</span>
                    <Select value={c.status || "draft"} onValueChange={(v) => setStatus(c, v)}>
                      <SelectTrigger className="h-8 text-xs w-[120px]" data-testid={`campaign-status-${c.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{["draft", "active", "paused", "completed"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <button className="text-destructive" onClick={() => remove(c.id)} data-testid={`delete-campaign-${c.id}`}><Trash size={15} /></button>
                  </div>
                </div>

                {rec.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {Object.entries(RECIPIENT_STATUS).map(([k, label]) => {
                      const n = rec.filter((r) => (r.status || "no_response") === k).length;
                      return n > 0 ? <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">{label}: {n}</span> : null;
                    })}
                    {conv > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent/15 text-accent border border-accent/30">Conversion {Math.round((conv / rec.length) * 100)}%</span>}
                  </div>
                )}

                {c.message && <pre className="mt-3 text-xs text-muted-foreground whitespace-pre-wrap line-clamp-4 font-sans">{c.message}</pre>}

                <Button asChild size="sm" variant="outline" className="rounded-full mt-3">
                  <Link to="/app/outreach/tracking" data-testid={`track-campaign-${c.id}`}><PaperPlaneTilt size={14} className="mr-1" /> Track responses</Link>
                </Button>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
